import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, FormControl, FormLabel, Input, Text,
  useToast, Divider, Box, HStack, Badge, Textarea, Checkbox,
} from '@chakra-ui/react';
import api from '../api';
import CompanySearch from './CompanySearch';

export default function AdminSettingsModal({ isOpen, onClose }) {
  const [settings, setSettings] = useState({
    max_comm_file_size_mb: '1024',
    max_image_size_kb: '300',
    max_document_size_mb: '25',
    tenant_company_id: '',
    tenant_company_name: '',
    feedback_approval_modal_text: 'By clicking Approve you confirm you are happy with this content and no further changes are required. This approval is final.',
    // Collaboration: when 'true' (default), non-admin users see a user
    // picker on the TODO page and can view colleagues' lists read-only.
    // When 'false', the picker is hidden — each user sees only their own
    // list. Admins are unaffected.
    see_others_todo: 'true',
    // Task lifecycle: completed or deleted tasks stay visible (greyed) for
    // this many days before the sweep hides them (do_not_show). Revealable
    // per-project via right-click "Show Deleted & Completed Tasks".
    show_completed_deleted_days: '7',
    // Public branding: shown on outward-facing surfaces (client upload
    // portals). Name falls back to the tenant company if left blank.
    public_company_name: '',
    public_company_logo_url: '',
    // Security: when 'true', uploaded files are virus-scanned before they are
    // stored. Off by default until a scanner (e.g. ClamAV) is provisioned.
    file_virus_check: 'false',
  });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      api.get('/settings').then(({ data }) => {
        setSettings(prev => ({
          ...prev,
          ...data,
          tenant_company_id: data.tenant_company_id || '',
          tenant_company_name: data.tenant_company_name || '',
          feedback_approval_modal_text: data.feedback_approval_modal_text || prev.feedback_approval_modal_text,
        }));
      }).catch(() => {});
    }
  }, [isOpen]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save all except the display-only company name
      const { tenant_company_name, ...toSave } = settings;
      await api.put('/settings', toSave);
      // Also save company name for display
      if (settings.tenant_company_name) {
        await api.put('/settings', { tenant_company_name: settings.tenant_company_name });
      }
      toast({ title: 'Settings saved', status: 'success', duration: 1500 });
      onClose();
    } catch (err) {
      toast({ title: 'Failed to save', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      {/* Fixed-inset geometry (10% rule): the modal can never exceed the
          screen — the body scrolls internally, header/footer stay pinned. */}
      <ModalContent position="fixed" top="10%" bottom="10%" left={{ base: '5%', md: '25%' }} right={{ base: '5%', md: '25%' }}
        w="auto" maxW="none" h="auto" maxH="none" my={0}
        display="flex" flexDirection="column" overflow="hidden">
        <ModalHeader fontSize="md">System Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody flex={1} overflowY="auto">
          <VStack spacing={4} align="stretch">

            {/* ── Tenant Company ── */}
            <Box>
              <Text fontSize="11px" fontWeight="700" color="gray.600" textTransform="uppercase"
                letterSpacing="wide" mb={2}>Your Organisation</Text>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>
                  Tenant Company
                  <Text as="span" fontSize="9px" color="gray.400" ml={1}>
                    (your own company — used as the invoicing entity and on bank accounts)
                  </Text>
                </FormLabel>
                <CompanySearch
                  value={settings.tenant_company_name}
                  companyId={settings.tenant_company_id || undefined}
                  onSelect={(company) => {
                    const id = company?.id;
                    setSettings(p => ({
                      ...p,
                      tenant_company_id: id ? String(id) : '',
                      tenant_company_name: company?.name || '',
                    }));
                  }}
                />
                {settings.tenant_company_id && (
                  <HStack mt={1}>
                    <Badge colorScheme="green" fontSize="9px">Linked</Badge>
                    <Text fontSize="10px" color="gray.500">{settings.tenant_company_name}</Text>
                  </HStack>
                )}
                {!settings.tenant_company_id && (
                  <Text fontSize="9px" color="orange.500" mt={1}>
                    Not set — search and select your company above
                  </Text>
                )}
              </FormControl>
            </Box>

            <Divider />

            {/* ── Client Engagement ── */}
            <Box>
              <Text fontSize="11px" fontWeight="700" color="gray.600" textTransform="uppercase"
                letterSpacing="wide" mb={2}>Client Engagement</Text>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>
                  Feedback Approval Confirmation Text
                  <Text as="span" fontSize="9px" color="gray.400" ml={1}>
                    (shown in the confirmation modal when a client clicks Approve)
                  </Text>
                </FormLabel>
                <Textarea size="sm" rows={4}
                  value={settings.feedback_approval_modal_text}
                  onChange={e => setSettings(p => ({ ...p, feedback_approval_modal_text: e.target.value }))}
                  placeholder="By clicking Approve you confirm…" />
              </FormControl>
            </Box>

            <Divider />
            <Box>
              <Text fontSize="11px" fontWeight="700" color="gray.600" textTransform="uppercase"
                letterSpacing="wide" mb={2}>Collaboration</Text>
              <FormControl>
                <Checkbox size="sm"
                  isChecked={String(settings.see_others_todo).toLowerCase() !== 'false'}
                  onChange={(e) => setSettings(p => ({ ...p, see_others_todo: e.target.checked ? 'true' : 'false' }))}>
                  <Text fontSize="xs">Allow users to view each other's To Do lists</Text>
                </Checkbox>
                <Text fontSize="9px" color="gray.400" ml={6} mt={0.5}>
                  When enabled (default), every user gets a dropdown on the To Do page to switch and view colleagues' lists read-only. When disabled, each user sees only their own list. Admins are unaffected and can always view all lists.
                </Text>
              </FormControl>
            </Box>

            <Divider />
            <Box>
              <Text fontSize="11px" fontWeight="700" color="gray.600" textTransform="uppercase"
                letterSpacing="wide" mb={2}>File Size Limits</Text>
              <VStack spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs">Max Communication File Size (MB)</FormLabel>
                  <Input size="sm" type="number" value={settings.max_comm_file_size_mb}
                    onChange={(e) => setSettings(p => ({ ...p, max_comm_file_size_mb: e.target.value }))} />
                  <Text fontSize="9px" color="gray.400">Default: 1024 MB (1 GB)</Text>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Max Image Size (KB)</FormLabel>
                  <Input size="sm" type="number" value={settings.max_image_size_kb}
                    onChange={(e) => setSettings(p => ({ ...p, max_image_size_kb: e.target.value }))} />
                  <Text fontSize="9px" color="gray.400">Images scaled down to fit. Default: 300 KB</Text>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Max Document File Size (MB)</FormLabel>
                  <Input size="sm" type="number" value={settings.max_document_size_mb}
                    onChange={(e) => setSettings(p => ({ ...p, max_document_size_mb: e.target.value }))} />
                  <Text fontSize="9px" color="gray.400">For invoices, notes, documents. Default: 25 MB</Text>
                </FormControl>
              </VStack>
            </Box>

            <Divider />
            <Box>
              <Text fontSize="11px" fontWeight="700" color="gray.600" textTransform="uppercase"
                letterSpacing="wide" mb={2}>Public Branding</Text>
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>Public Company Name</FormLabel>
                <Input size="sm" value={settings.public_company_name}
                  onChange={(e) => setSettings(p => ({ ...p, public_company_name: e.target.value }))} />
                <Text fontSize="10px" color="gray.500" mt={1}>
                  Shown to the public on client upload portals. Leave blank to use your Tenant Company name.
                </Text>
              </FormControl>
              <FormControl mt={3}>
                <FormLabel fontSize="sm" mb={1}>Public Company Logo URL</FormLabel>
                <Input size="sm" placeholder="https://…/logo.png" value={settings.public_company_logo_url}
                  onChange={(e) => setSettings(p => ({ ...p, public_company_logo_url: e.target.value }))} />
                <Text fontSize="10px" color="gray.500" mt={1}>
                  Full https:// URL of your logo image, displayed in the upload portal header. Leave blank for no logo.
                </Text>
              </FormControl>
            </Box>

            <Divider />
            <Box>
              <Text fontSize="11px" fontWeight="700" color="gray.600" textTransform="uppercase"
                letterSpacing="wide" mb={2}>Task Lifecycle</Text>
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>Show Completed or Deleted Tasks for (days)</FormLabel>
                <Input size="sm" type="number" min={0} max={365} w="120px"
                  value={settings.show_completed_deleted_days}
                  onChange={(e) => setSettings(p => ({ ...p, show_completed_deleted_days: e.target.value }))} />
                <Text fontSize="10px" color="gray.500" mt={1}>
                  Completed and deleted tasks stay visible (greyed) for this period, then hide automatically.
                  Right-click a project and choose "Show Deleted &amp; Completed Tasks" to reveal them again. Default: 7.
                </Text>
              </FormControl>
            </Box>

            <Divider />
            <Box>
              <Text fontSize="11px" fontWeight="700" color="gray.600" textTransform="uppercase"
                letterSpacing="wide" mb={2}>Security</Text>
              <FormControl>
                <Checkbox size="sm"
                  isChecked={String(settings.file_virus_check).toLowerCase() === 'true'}
                  onChange={(e) => setSettings(p => ({ ...p, file_virus_check: e.target.checked ? 'true' : 'false' }))}>
                  <Text fontSize="xs">File Virus Check</Text>
                </Checkbox>
                <Text fontSize="9px" color="gray.400" ml={6} mt={0.5}>
                  When enabled, uploaded files are scanned for malware before they are stored. Requires a scanner (ClamAV) to be provisioned on the server; until then this toggle has no effect on uploads.
                </Text>
              </FormControl>
            </Box>

          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
            onClick={handleSave} isLoading={saving}>Save Settings</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
